"use client";

import { type ReactNode, useMemo, useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/* ------------------------------------------------------------------ */
/*  Filler word list (module level) — multi-word phrases first so     */
/*  the regex alternation prefers the longest match.                  */
/* ------------------------------------------------------------------ */

const FILLER_WORDS: string[] = [
  "as a matter of fact",
  "at the end of the day",
  "for what it's worth",
  "to be honest",
  "in my opinion",
  "in order to",
  "of course",
  "you know",
  "i mean",
  "kind of",
  "sort of",
  "a lot",
  "a bit",
  "very",
  "really",
  "just",
  "actually",
  "basically",
  "literally",
  "quite",
  "rather",
  "somewhat",
  "definitely",
  "certainly",
  "totally",
  "simply",
  "extremely",
  "absolutely",
  "honestly",
  "truly",
  "seriously",
  "obviously",
  "clearly",
  "essentially",
  "virtually",
  "practically",
  "particularly",
  "fairly",
  "highly",
  "incredibly",
  "completely",
  "entirely",
];

const RE_FILLER = new RegExp(`\\b(?:${FILLER_WORDS.join("|")})\\b`, "gi");
const RE_SPLIT_WORDS = /\s+/;

/* ------------------------------------------------------------------ */
/*  Analysis                                                          */
/* ------------------------------------------------------------------ */

interface FillerFinding {
  word: string;
  count: number;
}

interface AnalysisResult {
  wordCount: number;
  totalFillers: number;
  density: number;
  findings: FillerFinding[];
  highlighted: ReactNode[];
}

function buildHighlighted(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(RE_FILLER)) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    nodes.push(
      <mark
        className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-900/60 dark:text-yellow-100"
        key={`filler-${match.index}`}
      >
        {match[0]}
      </mark>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

function analyzeFillers(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const counts = new Map<string, number>();
  let totalFillers = 0;
  for (const match of text.matchAll(RE_FILLER)) {
    const key = match[0].toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
    totalFillers += 1;
  }

  const wordCount = trimmed.split(RE_SPLIT_WORDS).filter(Boolean).length;
  const density = wordCount > 0 ? (totalFillers / wordCount) * 100 : 0;

  const findings: FillerFinding[] = Array.from(counts.entries())
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count);

  return {
    wordCount,
    totalFillers,
    density,
    findings,
    highlighted: buildHighlighted(text),
  };
}

/* ------------------------------------------------------------------ */
/*  Severity helper                                                   */
/* ------------------------------------------------------------------ */

function getSeverity(
  density: number,
  strings: typeof FILLER_WORD_FINDER_STRINGS
): {
  label: string;
  color: string;
  advice: string;
} {
  if (density < 3) {
    return {
      label: strings.tight,
      color:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      advice: strings.tightAdvice,
    };
  }
  if (density <= 6) {
    return {
      label: strings.moderate,
      color:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      advice: strings.moderateAdvice,
    };
  }
  return {
    label: strings.heavy,
    color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    advice: strings.heavyAdvice,
  };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Widget                                                            */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const FILLER_WORD_FINDER_STRINGS = {
  textAriaLabel: "Text to check for filler words",
  inputPlaceholder: "Paste or type your text here to find filler words...",
  totalWords: "Total Words",
  fillerWords: "Filler Words",
  uniqueFillers: "Unique Fillers",
  fillerDensity: "Filler Density",
  rating: "Rating",
  tight: "Tight",
  tightAdvice:
    "Your filler density is low. Your writing is lean — nice work keeping it tight.",
  moderate: "Moderate",
  moderateAdvice:
    "A moderate amount of filler. Review the highlights and cut the words that add no meaning — most sentences will read better without them.",
  heavy: "Heavy",
  heavyAdvice:
    "Your text leans heavily on filler words. Cutting most of the highlighted words will make your writing noticeably sharper and more confident.",
  yourTextHighlighted: "Your Text with Filler Words Highlighted",
  fillerWordsByFrequency: "Filler Words by Frequency",
  occurrenceWord: "one: occurrence|other: occurrences",
  noFillerWordsDetected: "No filler words detected!",
  noFillerWordsBody:
    "None of the common fillers on our list appear in your text. Lean writing!",
} as const;

export default function FillerWordFinderWidget() {
  const [text, setText] = useState("");

  const locale = useToolLocale();
  const strings = useToolUi(FILLER_WORD_FINDER_STRINGS);
  const fmt = useToolNumber();

  const analysis = useMemo(() => analyzeFillers(text), [text]);

  const severity = analysis ? getSeverity(analysis.density, strings) : null;

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textAriaLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.inputPlaceholder}
        value={text}
      />

      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.fillerWords}
            value={fmt(analysis.totalFillers)}
          />
          <StatCard
            label={strings.uniqueFillers}
            value={fmt(analysis.findings.length)}
          />
          <StatCard
            label={strings.fillerDensity}
            value={`${fmt(analysis.density, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
          />
        </div>
      )}

      {/* Severity */}
      {analysis && severity && (
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="mb-2 flex items-center gap-3">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.rating}
            </h2>
            <span
              className={`rounded-full px-2.5 py-0.5 font-medium text-xs ${severity.color}`}
            >
              {severity.label}
            </span>
          </div>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {severity.advice}
          </p>
        </div>
      )}

      {/* Highlighted text */}
      {analysis && analysis.totalFillers > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.yourTextHighlighted}
          </h2>
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {analysis.highlighted}
          </div>
        </section>
      )}

      {/* Per-word counts */}
      {analysis && analysis.findings.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.fillerWordsByFrequency}
          </h2>
          <div className="space-y-3">
            {analysis.findings.map((finding) => (
              <div
                className="flex items-center justify-between rounded-lg border border-border bg-card p-4"
                key={finding.word}
              >
                <span className="font-medium text-foreground text-sm">
                  {finding.word}
                </span>
                <span className="text-muted-foreground text-sm">
                  {fmt(finding.count)}
                  {tPlural(strings.occurrenceWord, locale, finding.count)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* None found */}
      {analysis && analysis.totalFillers === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noFillerWordsDetected}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.noFillerWordsBody}
          </p>
        </div>
      )}
    </>
  );
}
